/**
 * Material Library — Extensible registry of named material presets.
 *
 * Each preset is a factory function that creates a Three.js material.
 * New presets (e.g. 'carbon', 'chrome', 'brushed-metal') can be registered
 * at runtime without touching engine internals.
 *
 * Each preset can also register a `TypeValuesResolver` so the MaterialManager
 * can resolve lerp-target PBR values for type/finish transitions without
 * knowing anything about the active preset's internal structure.
 *
 * Usage:
 *   materialLibrary.create('car-paint', '#3a3a3a', { type: 'metallic' })
 *   materialLibrary.getTypeValues('car-paint', 'matte')  // → { roughness, ... }
 */

import * as THREE from 'three';
import { createCarPaintMaterial, getCarPaintPreset, type CarPaintType } from './presets/CarPaintPreset';
import { createCarbonMaterial, type CarbonType } from './presets/CarbonPreset';
import { createGlassMaterial, type GlassType, type GlassFactoryOptions } from './presets/GlassPreset';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Options passed to a material factory. Typed per preset, loosely typed here. */
export type MaterialFactoryOptions = Record<string, unknown>;

/**
 * A factory function that creates a Three.js material.
 * @param colorCode - Base color (HEX). May be ignored by some presets (e.g. carbon).
 * @param opts      - Preset-specific options.
 */
export type MaterialFactory = (
  colorCode: string,
  opts?: MaterialFactoryOptions,
) => THREE.Material;

/**
 * PBR values used as lerp targets during type/finish transitions.
 * Returned by `MaterialLibrary.getTypeValues()` for a given preset + type.
 *
 * `normalScale` is optional — presets without a normal map (e.g. carbon) omit it.
 * `MaterialManager` only sets `fromNormalScale`/`toNormalScale` when it is present.
 */
export interface MaterialTypeValues {
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  /** Normal map scale. Only present for presets that use a normal map (e.g. car-paint). */
  normalScale?: number;
}

/**
 * Resolves preset-specific PBR lerp-target values for a given `type` string.
 * Returns `undefined` when the type is unknown or the preset does not support
 * type-only lerp (the caller should treat this as a no-op).
 */
export type TypeValuesResolver = (type: string) => MaterialTypeValues | undefined;

// ---------------------------------------------------------------------------
// MaterialLibrary
// ---------------------------------------------------------------------------

export class MaterialLibrary {
  private readonly registry      = new Map<string, MaterialFactory>();
  private readonly typeResolvers = new Map<string, TypeValuesResolver>();

  /**
   * Register a named material preset.
   *
   * @param name     - Unique preset key (e.g. 'car-paint', 'carbon').
   * @param factory  - Factory that creates a Three.js material from colorCode + opts.
   * @param resolver - Optional: resolves PBR lerp-target values for a given type.
   *                   Required for presets that support type-only lerp transitions.
   */
  register(name: string, factory: MaterialFactory, resolver?: TypeValuesResolver): void {
    this.registry.set(name, factory);
    if (resolver) this.typeResolvers.set(name, resolver);
  }

  /** Returns true if a preset with the given name is registered. */
  has(name: string): boolean {
    return this.registry.has(name);
  }

  /**
   * Create a material from a named preset.
   * @throws Error if the preset is not registered.
   */
  create(name: string, colorCode: string, opts?: MaterialFactoryOptions): THREE.Material {
    const factory = this.registry.get(name);
    if (!factory) {
      throw new Error(
        `[MaterialLibrary] Unknown preset "${name}". ` +
          `Registered presets: ${[...this.registry.keys()].join(', ') || '(none)'}`,
      );
    }
    return factory(colorCode, opts);
  }

  /**
   * Resolve PBR lerp-target values for the given preset + type.
   *
   * Used by MaterialManager to build type-only lerp tasks without importing
   * preset-specific functions. Returns `undefined` if the preset has no
   * resolver or the type is unknown — the caller should skip the lerp.
   *
   * @param preset - Active preset name (e.g. 'car-paint', 'carbon').
   * @param type   - Finish/variant type string (e.g. 'metallic', 'glossy').
   */
  getTypeValues(preset: string, type: string): MaterialTypeValues | undefined {
    return this.typeResolvers.get(preset)?.(type);
  }
}

// ---------------------------------------------------------------------------
// Singleton with built-in presets
// ---------------------------------------------------------------------------

/** Shared instance with built-in presets pre-registered. */
export const materialLibrary = new MaterialLibrary();

materialLibrary.register(
  'car-paint',
  (colorCode, opts) => {
    const type = (opts?.type as CarPaintType | undefined) ?? 'metallic';
    const doubleSide = (opts?.doubleSide as boolean | undefined) ?? false;
    return createCarPaintMaterial(colorCode, type, { doubleSide });
  },
  (type) => getCarPaintPreset(type),
);

materialLibrary.register(
  'carbon',
  (colorCode, opts) => {
    const type = (opts?.type as CarbonType | undefined) ?? 'glossy';
    return createCarbonMaterial(colorCode, type);
  },
  // Carbon type changes require a full swap (glossy ↔ matte are structurally different).
  // A type-only lerp on carbon would produce visually incorrect results.
  // Returning undefined tells MaterialManager to skip the lerp — the swap path
  // (triggered by needsSwap in apply()) handles it instead.
  () => undefined,
);

materialLibrary.register(
  'glass',
  (colorCode, opts) => {
    const type = (opts?.type as GlassType | undefined) ?? 'clear';
    return createGlassMaterial(colorCode, type, opts as GlassFactoryOptions | undefined);
  },
  // Glass type changes require a full swap — transmission, colour and thickness
  // change substantially between tint levels. Returning undefined triggers the
  // swap path in MaterialManager (same pattern as carbon).
  () => undefined,
);
